import { useEffect, useState, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	type TeamData,
	fetchTeamData,
} from '@confluence/permissions-principals/entry-points/fetchTeamData.ts';

import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../Controller.tsx';

import type { PublishModalRestrictionsInput } from './publishModalDataToQueriesState.ts';
import type { useQueriesShareAndRestrictDialogQuery as useQueriesShareAndRestrictDialogQuery$data } from './__types__/useQueriesShareAndRestrictDialogQuery.ts';
import { ConfluenceGroupUsageType } from './__types__/useQueriesShareAndRestrictDialogQuery.ts';
import type { useContentPermissionsQuery$data as ContentPermissionsData } from './__generated__/useContentPermissionsQuery.graphql.ts';

export interface UseTeamDataResult {
	teamData: Record<string, TeamData> | null;
	isTeamDataLoading: boolean;
	teamDataError: Error | null;
}

export const useTeamData = (
	cloudId: string,
	publishModalRestrictions?: PublishModalRestrictionsInput,
	data?: useQueriesShareAndRestrictDialogQuery$data,
	contentPermissionsData?: ContentPermissionsData,
	shouldUseNewQuery?: boolean,
): UseTeamDataResult => {
	const [teamData, setTeamData] = useState<Record<string, TeamData> | null>(null);
	const [isTeamDataLoading, setIsTeamDataLoading] = useState(false);
	const [teamDataError, setTeamDataError] = useState<Error | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const teamIds = useMemo(() => {
		const teamIdSet = new Set<string>();

		if (publishModalRestrictions) {
			for (const item of publishModalRestrictions) {
				const groups = item?.restrictions?.group?.results ?? [];
				for (const group of groups) {
					if (group?.id && group.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION) {
						teamIdSet.add(group.id);
					}
				}
			}
			return Array.from(teamIdSet);
		}

		if (
			contentPermissionsData?.confluence_contentPermissions?.confluencePrincipalsConnection
				?.nodes &&
			shouldUseNewQuery
		) {
			for (const node of contentPermissionsData.confluence_contentPermissions
				.confluencePrincipalsConnection.nodes) {
				if (
					node?.principalId &&
					node.principalType === 'GROUP' &&
					node.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION
				) {
					teamIdSet.add(node.principalId);
				}
			}
			return Array.from(teamIdSet);
		}

		// Legacy path (share dialog)
		if (data?.content?.nodes?.[0]?.restrictions) {
			const readNodes = data.content.nodes[0].restrictions.read?.restrictions?.group?.nodes ?? [];
			const updateNodes =
				data.content.nodes[0].restrictions.update?.restrictions?.group?.nodes ?? [];
			const allNodes = [...readNodes, ...updateNodes];

			for (const node of allNodes) {
				if (node?.id && node?.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION) {
					teamIdSet.add(node.id);
				}
			}
		}

		return Array.from(teamIdSet);
	}, [publishModalRestrictions, data, contentPermissionsData, shouldUseNewQuery]);

	useEffect(() => {
		if (teamIds.length > 0) {
			setIsTeamDataLoading(true);
			setTeamDataError(null);

			fetchTeamData(cloudId, teamIds)
				.then((fetchedData) => {
					// Convert array to map where key is the team ID and value is the team data
					const teamDataMap: Record<string, TeamData> = {};
					fetchedData.forEach((teamItem) => {
						if (teamItem.id) {
							teamDataMap[teamItem.id] = teamItem;
						}
					});
					setTeamData(teamDataMap);
					setTeamDataError(null);
				})
				.catch((error) => {
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
							action: 'errored',
							actionSubject: 'useTeamData',
							attributes: {
								error,
								teamIds,
							},
						},
					}).fire();
					setTeamData(null);
					setTeamDataError(error instanceof Error ? error : new Error(String(error)));
				})
				.finally(() => {
					setIsTeamDataLoading(false);
				});
		} else {
			setTeamData(null);
			setIsTeamDataLoading(false);
			setTeamDataError(null);
		}
	}, [cloudId, teamIds, createAnalyticsEvent]);

	return {
		teamData,
		isTeamDataLoading,
		teamDataError,
	};
};
